import React, { useState, useEffect } from 'react';
import { useHistory } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import {
  Box,
  Container,
  Typography,
  Paper,
  Alert,
  TextField,
  InputAdornment,
  FormControlLabel,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Chip,
  CircularProgress,
  TablePagination,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
} from '@mui/material';
import {
  Search as SearchIcon,
  ArrowBack as ArrowBackIcon,
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Email as EmailIcon,
  Visibility as VisibilityIcon,
} from '@mui/icons-material';
import { format } from 'date-fns';
import { tr } from 'date-fns/locale';
import {
  getEmailTemplates,
  deleteEmailTemplate,
  previewEmailTemplate,
} from '../../redux/slices/emailTemplateSlice';
import { useHasPermission } from '../../hooks/usePermission';
import ForbiddenPage from '../ForbiddenPage';
import alertify from 'alertifyjs';

/**
 * EmailTemplateListPage - Admin email templates page
 *
 * Features:
 * - Email template table with filtering (isActive)
 * - Search by key, title, subject
 * - Create, Edit, Delete (soft delete) actions
 * - HTML content preview
 * - Pagination
 *
 * Permission: admin.email-templates.view
 */
const EmailTemplateListPage = () => {
  const dispatch = useDispatch();
  const history = useHistory();

  const { templates, loading, error, pagination } = useSelector(
    (state) => state.emailTemplates
  );

  const hasViewPermission = useHasPermission('admin.email-templates.view');
  const hasCreatePermission = useHasPermission('admin.email-templates.create');
  const hasEditPermission = useHasPermission('admin.email-templates.edit');
  const hasDeletePermission = useHasPermission('admin.email-templates.delete');

  const [searchTerm, setSearchTerm] = useState('');
  const [isActiveFilter, setIsActiveFilter] = useState(true);
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [page, setPage] = useState(0);
  const [limit, setLimit] = useState(10);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [templateToDelete, setTemplateToDelete] = useState(null);
  const [previewDialogOpen, setPreviewDialogOpen] = useState(false);
  const [previewContent, setPreviewContent] = useState({ subject: '', html: '' });

  // Debounce search term (500ms)
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchTerm);
    }, 500);

    return () => clearTimeout(timer);
  }, [searchTerm]);

  // Fetch email templates
  useEffect(() => {
    if (hasViewPermission) {
      const params = {
        search: debouncedSearch || undefined,
        isActive: isActiveFilter,
        page: page + 1,
        limit,
      };

      Object.keys(params).forEach((key) => {
        if (params[key] === undefined || params[key] === '') {
          delete params[key];
        }
      });

      dispatch(getEmailTemplates(params));
    }
  }, [dispatch, debouncedSearch, isActiveFilter, hasViewPermission, page, limit]);

  const handlePageChange = (event, newPage) => {
    setPage(newPage);
  };

  const handleLimitChange = (event) => {
    setLimit(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleCreateNew = () => {
    history.push('/admin/email-templates/new');
  };

  const handleEdit = (id) => {
    history.push(`/admin/email-templates/${id}/edit`);
  };

  const handleDeleteClick = (template) => {
    setTemplateToDelete(template);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!templateToDelete) return;

    try {
      await dispatch(deleteEmailTemplate(templateToDelete._id)).unwrap();
      alertify.success('Email şablonu başarıyla pasifleştirildi');
      setDeleteDialogOpen(false);
      setTemplateToDelete(null);
      // Refresh list
      dispatch(getEmailTemplates({
        search: debouncedSearch || undefined,
        isActive: isActiveFilter,
        page: page + 1,
        limit,
      }));
    } catch (err) {
      alertify.error(err.message || 'Email şablonu pasifleştirilemedi');
    }
  };

  const handlePreviewClick = async (template) => {
    try {
      const result = await dispatch(
        previewEmailTemplate({ id: template._id, variables: {} })
      ).unwrap();
      setPreviewContent(result);
      setPreviewDialogOpen(true);
    } catch (err) {
      alertify.error(err.message || 'Önizleme oluşturulamadı');
    }
  };

  if (!hasViewPermission) {
    return <ForbiddenPage />;
  }

  return (
    <Container maxWidth="xl">
      <Box sx={{ py: 4 }}>
        {/* Header */}
        <Box sx={{ mb: 3, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <IconButton onClick={() => history.goBack()}>
              <ArrowBackIcon />
            </IconButton>
            <EmailIcon sx={{ fontSize: 32, color: 'primary.main' }} />
            <Box>
              <Typography variant="h4" fontWeight="bold">
                Email Şablonları
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Sistemdeki email şablonlarını yönetin
              </Typography>
            </Box>
          </Box>
          {hasCreatePermission && (
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={handleCreateNew}
            >
              Yeni Şablon Oluştur
            </Button>
          )}
        </Box>

        {/* Filters */}
        <Paper sx={{ p: 2, mb: 3 }}>
          <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center' }}>
            <TextField
              placeholder="Anahtar, başlık veya konu ara..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              size="small"
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
              }}
              sx={{ flex: 1, minWidth: 250 }}
            />
            <FormControlLabel
              control={
                <Switch
                  checked={isActiveFilter}
                  onChange={(e) => setIsActiveFilter(e.target.checked)}
                  name="isActiveFilter"
                  color="primary"
                />
              }
              label="Aktif Şablonlar"
            />
          </Box>
        </Paper>

        {/* Error Alert */}
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {/* Table */}
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Anahtar</TableCell>
                <TableCell>Başlık</TableCell>
                <TableCell>Konu</TableCell>
                <TableCell>Durum</TableCell>
                <TableCell>Oluşturulma Tarihi</TableCell>
                <TableCell>Güncellenme Tarihi</TableCell>
                <TableCell align="right">İşlemler</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={7} align="center" sx={{ py: 4 }}>
                    <CircularProgress />
                  </TableCell>
                </TableRow>
              ) : templates.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} align="center" sx={{ py: 4 }}>
                    <Typography color="text.secondary">
                      Email şablonu bulunamadı
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                templates.map((template) => (
                  <TableRow key={template._id}>
                    <TableCell>{template.key}</TableCell>
                    <TableCell>{template.title}</TableCell>
                    <TableCell>{template.subject}</TableCell>
                    <TableCell>
                      <Chip
                        label={template.isActive ? 'Aktif' : 'Pasif'}
                        color={template.isActive ? 'success' : 'default'}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>
                      {template.createdAt
                        ? format(new Date(template.createdAt), 'dd MMM yyyy HH:mm', { locale: tr })
                        : '-'}
                    </TableCell>
                    <TableCell>
                      {template.updatedAt
                        ? format(new Date(template.updatedAt), 'dd MMM yyyy HH:mm', { locale: tr })
                        : '-'}
                    </TableCell>
                    <TableCell align="right">
                      <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
                        <Tooltip title="Önizle">
                          <IconButton
                            size="small"
                            onClick={() => handlePreviewClick(template)}
                          >
                            <VisibilityIcon />
                          </IconButton>
                        </Tooltip>
                        {hasEditPermission && (
                          <Tooltip title="Düzenle">
                            <IconButton
                              size="small"
                              onClick={() => handleEdit(template._id)}
                            >
                              <EditIcon />
                            </IconButton>
                          </Tooltip>
                        )}
                        {hasDeletePermission && (
                          <Tooltip title={template.isActive ? "Pasifleştir" : "Aktifleştir"}>
                            <IconButton
                              size="small"
                              color={template.isActive ? "error" : "default"}
                              onClick={() => handleDeleteClick(template)}
                            >
                              <DeleteIcon />
                            </IconButton>
                          </Tooltip>
                        )}
                      </Box>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>

        {/* Pagination */}
        <TablePagination
          component="div"
          count={pagination.total}
          page={page}
          onPageChange={handlePageChange}
          rowsPerPage={limit}
          onRowsPerPageChange={handleLimitChange}
          rowsPerPageOptions={[5, 10, 25, 50]}
          labelRowsPerPage="Sayfa başına satır:"
          labelDisplayedRows={({ from, to, count }) =>
            `${from}-${to} / ${count}`
          }
        />

        {/* Delete Confirmation Dialog */}
        <Dialog
          open={deleteDialogOpen}
          onClose={() => setDeleteDialogOpen(false)}
        >
          <DialogTitle>Şablonu Pasifleştir</DialogTitle>
          <DialogContent>
            <Typography>
              "{templateToDelete?.title}" başlıklı email şablonunu pasifleştirmek istediğinizden emin misiniz?
              Pasifleştirilen şablonlar email gönderiminde kullanılmayacaktır.
            </Typography>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setDeleteDialogOpen(false)}>İptal</Button>
            <Button onClick={handleDeleteConfirm} color="error" variant="contained">
              Pasifleştir
            </Button>
          </DialogActions>
        </Dialog>

        {/* HTML Preview Dialog */}
        <Dialog
          open={previewDialogOpen}
          onClose={() => setPreviewDialogOpen(false)}
          maxWidth="md"
          fullWidth
        >
          <DialogTitle>Email Şablonu Önizleme: {previewContent.subject}</DialogTitle>
          <DialogContent dividers>
            <iframe
              srcDoc={previewContent.html}
              title="Email Preview"
              style={{ width: '100%', height: '500px', border: 'none' }}
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setPreviewDialogOpen(false)}>Kapat</Button>
          </DialogActions>
        </Dialog>
      </Box>
    </Container>
  );
};

export default EmailTemplateListPage;

